import { Component, Input, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { RiyalSymbolComponent } from '../../../../../shared/core/ui/riyal-symbol/riyal-symbol.component';
import { DateFormatPipe } from '../../../../../trash/shared/pipes/date-format.pipe';
import { TimeFormatPipe } from '../../../../../trash/shared/pipes/time-format.pipe';
import { PublicService } from '../../../../../trash/shared/services/public.service';
import { IReservationListResponse } from '../../../dtos';
import { CountdownTimerComponent } from '../../../../../shared/components/countdown-timer/countdown-timer.component';

type TicketStatusPillClass =
  | 'status-active'
  | 'status-cancelled'
  | 'status-settled'
  | 'status-completed'

@Component({
  selector: 'app-parking-ticket-card',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    TranslateModule,
    RiyalSymbolComponent,
    DateFormatPipe,
    TimeFormatPipe,
    CountdownTimerComponent,
  ],
  templateUrl: './parking-ticket-card.component.html',
  styleUrl: './parking-ticket-card.component.scss',
})
export class ParkingTicketCardComponent {
  private static readonly SUCCESS_TICKET_STATUSES = new Set([
    'Settled',
    'Completed',
  ]);

  @Input({ required: true }) ticket!: IReservationListResponse;

  publicService: PublicService = inject(PublicService);

  isSettledTicket(): boolean {
    return this.ticket.isSettled;
  }

  getDurationHours(startTime: Date | string, endTime: Date | string): string {
    const start = new Date(startTime);
    const end = new Date(endTime);

    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return '-';
    }

    const isArabic = this.publicService.getCurrentLang() === 'ar';

    let diffInMs = Math.max(0, end.getTime() - start.getTime());
    const totalMinutes = Math.floor(diffInMs / (1000 * 60));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    const formattedHours = hours.toString().padStart(2, '0');
    // Only append minutes if there are any
    if (minutes > 0) {
      const formattedMinutes = minutes.toString().padStart(2, '0');
      if (isArabic) {
        // Arabic: use RTL ordering and س for hours, د for minutes
        return `${formattedHours} س : ${formattedMinutes} د`;
      } else {
        return `${formattedHours} hr : ${formattedMinutes} min`;
      }
    } else {
      if (isArabic) {
        return `${formattedHours} س`;
      } else {
        return `${formattedHours} hr`;
      }
    }
  }

  getStatusTranslationKey(status: string | undefined): string {
    switch (status) {
      case 'Active':
        return 'HOME.TICKET_STATUS_ACTIVE';
      case 'Cancelled':
        return 'HOME.TICKET_STATUS_CANCELLED';
      case 'Settled':
        return 'HOME.TICKET_STATUS_SETTLED';
      default:
        return 'HOME.TICKET_STATUS_COMPLETED';
    }
  }

  getStatusPillClass(status: string | undefined): TicketStatusPillClass {
    switch (status) {
      case 'Active':
        return 'status-active';
      case 'Cancelled':
        return 'status-cancelled';
      case 'Settled':
        return 'status-settled';
        default:
        return 'status-completed';
    }
  }

  shouldShowStatusCheck(status: string | undefined): boolean {
    return ParkingTicketCardComponent.SUCCESS_TICKET_STATUSES.has(status ?? '');
  }

  isZeroAmount(amount: number | undefined | null): boolean {
    return (amount ?? 0) <= 0;
  }
}
